import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useGetEventByDateRangeQuery } from 'services/event.service';
import dayjs from 'dayjs';
import DropdownFilter from 'components/v2/DropdownFilter/DropdownFilter';
import { cn } from 'utils/helpers';
import { useGetAllEventsQuery } from 'services/event.service';
import EventCard from './EventCard';
import { useNavigate } from 'react-router-dom';
import EmptyEventsList from './EmptyEventsList';
import InfiniteList from 'modules/home/pages/homePage/InfiniteList';
import { Loader2 } from 'lucide-react';
import { EventStatus } from 'utils/constants';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import useUpdates from '../hooks/useUpdates';
import { IEvent } from 'types/event';

type Props = {
  sortOptions: { label: string; value: string }[];
  sortedEvents: { label: string; value: string };
  setSortedEvents: (value: { label: string; value: string }) => void;
};

export const compareEventDates = (a: any, b: any): number =>
  dayjs(b.event.start).diff(dayjs(a.event.start));

const UpcomingEventsSection = ({
  sortOptions,
  sortedEvents,
  setSortedEvents,
}: Props) => {
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  const { data: allEvents, isFetching } = useGetAllEventsQuery({
    page,
    history: 'false',
  });

  const { updates } = useUpdates();

  const getDateRange = (offset: number, unit: dayjs.ManipulateType) => ({
    startDate: dayjs()
      .startOf('week')
      .add(1, 'day')
      .add(offset, unit)
      .format('YYYY-MM-DD'),
    endDate: dayjs()
      .endOf('week')
      .add(1, 'day')
      .add(offset, unit)
      .format('YYYY-MM-DD'),
  });
  const inviteEvents = allEvents?.filter((event: any) => {
    return event.event?.contacts.some(
      (contact: any) =>
        contact.phoneNumber === user?.phoneNumber &&
        contact.status === EventStatus.Accepted,
    );
  });

  const { data: thisWeekEvents, isFetching: isThisWeekFetching } =
    useGetEventByDateRangeQuery(getDateRange(0, 'week'), {
      skip: sortedEvents.value !== 'this-week',
    });

  const { data: nextWeekEvents, isFetching: isNextWeekFetching } =
    useGetEventByDateRangeQuery(getDateRange(1, 'week'), {
      skip: sortedEvents.value !== 'next-week',
    });

  const { data: nextMonthEvents, isFetching: isNextMonthFetching } =
    useGetEventByDateRangeQuery(getDateRange(1, 'month'), {
      skip: sortedEvents.value !== 'next-month',
    });

  const filterEvents = (events: IEvent[] = []) => {
    return events.filter((event: any) => {
      return dayjs(event?.event?.end).isAfter(dayjs());
    });
  };

  const isRangeFetching =
    isThisWeekFetching || isNextWeekFetching || isNextMonthFetching;

  const memoizedEvents = useMemo(() => {
    let eventsToReturn: IEvent[] = [];
    if (sortedEvents.value === 'this-week') {
      eventsToReturn = filterEvents(thisWeekEvents) || [];
    } else if (sortedEvents.value === 'next-week') {
      eventsToReturn = filterEvents(nextWeekEvents) || [];
    } else if (sortedEvents.value === 'next-month') {
      eventsToReturn = filterEvents(nextMonthEvents) || [];
    } else {
      eventsToReturn = [];
    }

    // Create a proper copy before sorting to avoid modifying read-only arrays
    const eventsCopy = [...eventsToReturn];

    return eventsCopy.sort(compareEventDates);
  }, [sortedEvents, thisWeekEvents, nextWeekEvents, nextMonthEvents]);

  const memoizedRecentEvents = useMemo(() => {
    const uniqueEvents = [...(inviteEvents || [])].filter(
      (event, index, self) =>
        index === self.findIndex((e) => e.event._id === event.event._id),
    );

    return uniqueEvents.sort(compareEventDates);
  }, [inviteEvents]);

  useEffect(() => {
    if (inviteEvents && inviteEvents.length > 0) {
      setRecentEvents((prev) => {
        const newEvents = inviteEvents.filter(
          (event: any) => !prev.some((e) => e.event._id === event.event._id),
        );

        if (newEvents.length === 0) return prev;

        return [...prev, ...newEvents].sort(compareEventDates);
      });
    }
  }, [inviteEvents]);

  return (
    <div>
      <div className="w-full flex justify-between items-center px-5 pb-3">
        <p className="text-[1rem] font-bold">
          {(() => {
            switch (sortedEvents.value) {
              case 'most-recent':
                return recentEvents?.length > 0
                  ? `${recentEvents?.length} events`
                  : 'Events';
              default:
                return memoizedEvents?.length > 0
                  ? `${memoizedEvents?.length} events`
                  : 'Events';
            }
          })()}
        </p>
        <div className="flex items-center">
          <DropdownFilter
            options={sortOptions}
            selectedValue={sortedEvents}
            setSelectedValue={setSortedEvents}
          />
        </div>
      </div>
      <div
        className={cn(
          'w-full max-h-[42svh] min-h-[21.75rem] flex flex-col gap-3 overflow-y-scroll',
          updates.length === 0 && 'max-h-[55svh]',
        )}
      >
        <InfiniteList
          onInView={() => {
            if (isFetching || !hasMore) return;

            if (sortedEvents.value === 'most-recent') {
              setPage((prev) => prev + 1);

              setRecentEvents((prev) => {
                const newEvents = memoizedRecentEvents.filter(
                  (event: any) =>
                    !prev.some((e) => e.event._id === event.event._id),
                );

                if (newEvents.length === 0) {
                  setHasMore(false);
                  return prev;
                }

                return [...prev, ...newEvents].sort(compareEventDates);
              });
            }
          }}
          children={
            <div
              className={cn(
                'w-full flex flex-col justify-center items-center gap-3 overflow-y-scroll mb-[9rem] px-5',
              )}
            >
              {sortedEvents.value !== 'most-recent' ? (
                memoizedEvents?.length > 0 ? (
                  memoizedEvents.map((event: any, index: number) => (
                    <div
                      className="w-full flex"
                      key={index}
                      onClick={() => {
                        navigate(`/lets-meet/view/${event.event._id}`);
                      }}
                    >
                      <EventCard
                        title={event.event.activity}
                        description={event.event.description}
                        startTime={event.event.start}
                        endTime={event.event.end}
                        emoji={event.event.emoji || '📅'}
                        status={event.event.status}
                        organizerId={event.event.organizerId}
                      />
                    </div>
                  ))
                ) : (
                  <>
                    {!isRangeFetching && !memoizedEvents?.length && (
                      <EmptyEventsList />
                    )}
                    {isRangeFetching && (
                      <div className="flex justify-center items-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                  </>
                )
              ) : null}
              {sortedEvents.value === 'most-recent' ? (
                recentEvents?.length > 0 ? (
                  recentEvents.map((event: any, index: number) => (
                    <div
                      className="w-full flex"
                      key={index}
                      onClick={() => {
                        navigate(`/lets-meet/view/${event.event._id}`);
                      }}
                    >
                      <EventCard
                        title={event.event.activity}
                        description={event.event.description}
                        startTime={event.event.start}
                        endTime={event.event.end}
                        emoji={event.event.emoji}
                        status={event.event.status}
                        organizerId={event.event.userId}
                      />
                    </div>
                  ))
                ) : (
                  <>
                    {!isFetching && recentEvents.length === 0 && (
                      <EmptyEventsList />
                    )}
                    {isFetching && (
                      <div className="flex justify-center items-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                  </>
                )
              ) : null}
            </div>
          }
        />
        {(isFetching || isRangeFetching) && (
          <p className="text-center text-gray-500">Loading...</p>
        )}
      </div>
    </div>
  );
};

export default UpcomingEventsSection;
